import { useState } from 'react';
import { Calendar, Building2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { useChargebackStore } from '../../stores/chargebackStore';

const OEMS = [
  'Ford', 'GM', 'Stellantis', 'Toyota', 'Honda', 'Nissan',
  'BMW', 'Mercedes', 'VW', 'Tesla'
];

// Define quarters for 2023, 2024, and 2025
const QUARTERS = [
  // 2023
  { label: 'Q1 2023', year: 2023, quarter: 1, start: '2023-01-01', end: '2023-03-31' },
  { label: 'Q2 2023', year: 2023, quarter: 2, start: '2023-04-01', end: '2023-06-30' },
  { label: 'Q3 2023', year: 2023, quarter: 3, start: '2023-07-01', end: '2023-09-30' },
  { label: 'Q4 2023', year: 2023, quarter: 4, start: '2023-10-01', end: '2023-12-31' },
  // 2024
  { label: 'Q1 2024', year: 2024, quarter: 1, start: '2024-01-01', end: '2024-03-31' },
  { label: 'Q2 2024', year: 2024, quarter: 2, start: '2024-04-01', end: '2024-06-30' },
  { label: 'Q3 2024', year: 2024, quarter: 3, start: '2024-07-01', end: '2024-09-30' },
  { label: 'Q4 2024', year: 2024, quarter: 4, start: '2024-10-01', end: '2024-12-31' },
  // 2025
  { label: 'Q1 2025', year: 2025, quarter: 1, start: '2025-01-01', end: '2025-03-31' },
  { label: 'Q2 2025', year: 2025, quarter: 2, start: '2025-04-01', end: '2025-06-30' },
  { label: 'Q3 2025', year: 2025, quarter: 3, start: '2025-07-01', end: '2025-09-30' },
  { label: 'Q4 2025', year: 2025, quarter: 4, start: '2025-10-01', end: '2025-12-31' },
];

export default function Step1_OemPeriod({ onNext }) {
  const {
    selectedOem,
    periodStart,
    periodEnd,
    setOem,
    setPeriod,
    isStep1Valid
  } = useChargebackStore();

  const [isCustom, setIsCustom] = useState(false);
  const [selectedQuarter, setSelectedQuarter] = useState(null);
  const [activeYear, setActiveYear] = useState(2024); // Default to current year

  const handleQuarterClick = (quarter) => {
    setSelectedQuarter(quarter.label);
    setPeriod(quarter.start, quarter.end);
    setIsCustom(false);
  };

  const handleCustomClick = () => {
    setIsCustom(true);
    setSelectedQuarter(null);
    // Clear dates when switching to custom
    setPeriod('', '');
  };

  const handleYearChange = (year) => {
    setActiveYear(year);
  };

  const handleNext = () => {
    if (isStep1Valid()) {
      onNext();
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Building2 size={24} className="text-blue-600" />
          Step 1: Select OEM & Period
        </CardTitle>
        <CardDescription>
          Choose the OEM and time period for this chargeback
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* OEM Selection */}
        <div className="space-y-2">
          <Label htmlFor="oem" className="text-base font-semibold">
            OEM / Customer *
          </Label>
          <Select value={selectedOem || ''} onValueChange={setOem}>
            <SelectTrigger id="oem" className="w-full">
              <SelectValue placeholder="Select OEM..." />
            </SelectTrigger>
            <SelectContent>
              {OEMS.map(oem => (
                <SelectItem key={oem} value={oem}>{oem}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Period Selection */}
        <div className="space-y-3">
          <Label className="text-base font-semibold">
            Time Period *
          </Label>
          
          {/* Year Tabs */}
          <div className="flex gap-2 border-b border-gray-200 pb-2">
            {[2023, 2024, 2025].map(year => (
              <button
                key={year}
                type="button"
                onClick={() => handleYearChange(year)}
                className={`
                  px-4 py-2 text-sm font-medium rounded-t-lg transition-colors
                  ${activeYear === year
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }
                `}
              >
                {year}
              </button>
            ))}
          </div>

          {/* Quarter Buttons for Active Year */}
          <div className="grid grid-cols-4 gap-2">
            {QUARTERS.filter(q => q.year === activeYear).map(quarter => (
              <Button
                key={quarter.label}
                type="button"
                variant={selectedQuarter === quarter.label ? 'default' : 'outline'}
                onClick={() => handleQuarterClick(quarter)}
                className="w-full"
              >
                Q{quarter.quarter}
              </Button>
            ))}
          </div>

          {/* Custom Period Button */}
          <div className="pt-2">
            <Button
              type="button"
              variant={isCustom ? 'default' : 'outline'}
              onClick={handleCustomClick}
              className="w-full flex items-center justify-center gap-2"
            >
              <Calendar size={16} />
              Custom Period
            </Button>
          </div>
        </div>

        {/* Custom Date Range Inputs */}
        {isCustom && (
          <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="space-y-2">
              <Label htmlFor="start-date" className="text-sm font-medium">
                Start Date *
              </Label>
              <Input
                id="start-date"
                type="date"
                value={periodStart || ''}
                onChange={(e) => setPeriod(e.target.value, periodEnd || '')}
                className="w-full"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="end-date" className="text-sm font-medium">
                End Date *
              </Label>
              <Input
                id="end-date"
                type="date"
                value={periodEnd || ''}
                onChange={(e) => setPeriod(periodStart || '', e.target.value)}
                className="w-full"
              />
            </div>
          </div>
        )}

        {/* Summary */}
        {isStep1Valid() && (
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-900">
              <strong>Selected:</strong> {selectedOem} •{' '}
              {new Date(periodStart).toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'short', 
                day: 'numeric' 
              })} - {new Date(periodEnd).toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'short', 
                day: 'numeric' 
              })}
            </p>
          </div>
        )}

        {/* Next Button */}
        <div className="flex justify-end pt-4">
          <Button
            onClick={handleNext}
            disabled={!isStep1Valid()}
            size="lg"
          >
            Continue to Part Selection →
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
